import { lazy } from 'react';

import { isNil } from 'lodash';

import { MILLISECONDS_PER_SECOND, SECONDS_PER_MINUTE } from 'constants/date';
import { logger } from 'utils/logger';
import { reloadPage } from 'utils/pageReload';
import { getIsNativePlatform } from 'utils/platform';

const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';
const CHUNK_LOAD_MESSAGE_PATTERN = /Loading (CSS )?chunk \S+ failed/i;
const RELOAD_GUARD_STORAGE_KEY = 'ssChunkLoadReloadAt';
const RELOAD_GUARD_WINDOW_MS = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
const FIRST_RETRY_DELAY_MS = 500;
const SECOND_RETRY_DELAY_MS = 1_500;
const RETRY_DELAYS_MS = [FIRST_RETRY_DELAY_MS, SECOND_RETRY_DELAY_MS];
// A reload the user cancels (a beforeunload "Leave site?" prompt) must not strand the page. Long
// enough that a reload which is merely slow (post-deploy) lands before the error UI would show.
export const RELOAD_FALLBACK_MS = 10_000;

export const isChunkLoadError = error =>
  error?.name === CHUNK_LOAD_ERROR_NAME || CHUNK_LOAD_MESSAGE_PATTERN.test(error?.message ?? '');

const readLastReloadAt = () => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLastReloadAt = timestamp => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_STORAGE_KEY, String(timestamp));

    return true;
  } catch {
    return false;
  }
};

/* A chunk that still fails after retries almost always means a deploy replaced the build while this
   tab was open: the running runtime asks for a hashed file that no longer exists (the SPA catch-all
   then answers with index.html). Only a full reload picks up the new runtime. The reload is guarded
   per tab so a genuinely broken chunk cannot loop, and skipped when the guard cannot be written.
   Native shells bundle their assets and a reload would tear down the persistent JS context, so they
   never reload here. Returns whether a reload was started. */
export const reloadOnceForChunkError = () => {
  if (getIsNativePlatform()) {
    return false;
  }

  const now = Date.now();

  if (now - readLastReloadAt() < RELOAD_GUARD_WINDOW_MS || !writeLastReloadAt(now)) {
    return false;
  }

  logger.warn('Reloading the page to recover from a failed chunk load');
  reloadPage();

  return true;
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const importWithRetry = async (importComponent, retryDelays) => {
  try {
    return await importComponent();
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    const [nextDelay, ...remainingDelays] = retryDelays;

    if (isNil(nextDelay)) {
      if (reloadOnceForChunkError()) {
        // Keep the Suspense fallback up while the reload replaces the page; surface the error if the
        // reload was cancelled and the page is still here.
        return new Promise((_, reject) => {
          setTimeout(() => reject(error), RELOAD_FALLBACK_MS);
        });
      }

      throw error;
    }

    await wait(nextDelay);

    return importWithRetry(importComponent, remainingDelays);
  }
};

export const lazyWithRetry = importComponent =>
  lazy(() => importWithRetry(importComponent, RETRY_DELAYS_MS));
